import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import api from "../../api/axios";
import { Link } from 'react-router-dom';
import { Loader2, ShoppingBag, CheckCircle, ArrowRight, ShieldCheck, Truck, Headphones, Lock } from 'lucide-react';

const CartPage = () => {
  const colors = {
    primary: "#FFAA00",
    secondary: "#8A8A89",
    dark: "#1A1A1A"
  };

  const BACKEND_BASE = process.env.REACT_APP_BACKEND_URL;
  const formRef = useRef(null);

  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCheckoutVisible, setIsCheckoutVisible] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(null);
  
  // New state to track delivery mode cleanly
  const [deliveryMode, setDeliveryMode] = useState("magasin"); 

  const getImageUrl = (path) => {
    if (!path) return "";
    if (path.startsWith("http")) return path;
    if (path.startsWith("/storage")) return `${BACKEND_BASE}${path}`;
    return `${BACKEND_BASE}/storage/${path}`;
  };

  useEffect(() => {
    const loadCart = async () => {
      try {
        const storedCart = JSON.parse(localStorage.getItem("my_cart")) || [];
        if (storedCart.length === 0) {
          setCartItems([]);
          setLoading(false);
          return;
        }
        const res = await api.get("all_products");
        const products = res.data.data;
        const cartProducts = products
          .filter(p => storedCart.includes(p.slug))
          .map(p => ({
            id: p.id,
            slug: p.slug,
            title: p.title,
            price: Number(p.price),
            image: p.main_image,
            quantity: 1,
            stock: p.stock
          }));
        setCartItems(cartProducts);
      } catch (err) {
        console.error("Cart load error:", err);
      } finally {
        setLoading(false);
      }
    };
    loadCart();
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const updateQuantity = (id, delta) => {
    setCartItems(prev =>
      prev.map(item => {
        if (item.id === id) {
          const newQuantity = item.quantity + delta;
          if (newQuantity < 1) return item;
          if (newQuantity > item.stock) {
            alert(`Désolé, il n'y a que ${item.stock} articles en stock.`);
            return item;
          }
          return { ...item, quantity: newQuantity };
        }
        return item;
      })
    );
  };

  const removeItem = (slug) => {
    const updatedCart = cartItems.filter(item => item.slug !== slug);
    setCartItems(updatedCart);
    const slugs = updatedCart.map(item => item.slug);
    localStorage.setItem("my_cart", JSON.stringify(slugs));
    window.dispatchEvent(new Event("cartUpdated"));
  };

  const subtotal = cartItems.reduce((acc, item) => {
    return item.stock > 0 ? acc + (item.price * item.quantity) : acc;
  }, 0);

  const total = subtotal;

  const productsQueryString = cartItems
    .filter(item => item.stock > 0)
    .map(item => `${item.slug}(${item.quantity})`)
    .join(', ');

  const handleOrderSubmit = async (e) => {
    e.preventDefault();

    if (subtotal === 0) {
      alert("Votre panier ne contient aucun article disponible.");
      return;
    }

    setIsSubmitting(true);
    const formData = new FormData(e.target);
    
    // Base payload
    const payload = {
      name: formData.get('name'),
      phone: formData.get('phone'),
      city: formData.get('city'),
      email: formData.get('email'),
      mode_reception: formData.get('mode_reception'),
      order_details: productsQueryString,
    };

    // Conditionally add delivery details to the payload if "livraison" is selected
    if (deliveryMode === "livraison") {
      payload.adresse = formData.get('adresse');
      payload.quartier = formData.get('quartier');
      payload.commentaire = formData.get('commentaire');
    }

    try {
      const res = await api.post('/place-order', payload);
      if (res.data.success) {
        setOrderSuccess(res.data.order_number);
        localStorage.removeItem("my_cart");
        setCartItems([]);
        window.dispatchEvent(new Event("cartUpdated"));
      }
    } catch (err) {
      alert("Erreur lors de la validation. Veuillez vérifier vos informations.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="animate-spin text-orange-400" size={40} />
    </div>
  );

  if (orderSuccess) return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="text-center max-w-md bg-white p-10 rounded-[3rem] shadow-2xl border border-gray-50">
        <CheckCircle className="mx-auto text-green-500 mb-6" size={80} />
        <h2 className="text-3xl font-black uppercase text-gray-900 mb-2">Merci !</h2>
        <p className="text-gray-500 font-bold mb-6">Votre commande <span className="text-black">#{orderSuccess}</span> a été reçue. Nous vous contacterons bientôt.</p>
        <Link to="/" className="inline-flex items-center gap-2 bg-[#ffaa00] text-white px-8 py-4 rounded-2xl font-black uppercase tracking-widest text-xs shadow-lg shadow-orange-100">
          Retour à la boutique <ArrowRight size={16} />
        </Link>
      </motion.div>
    </div>
  );

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8 font-sans bg-[#F9FAFB]">
      <div className="max-w-6xl mx-auto">
        <header className="mb-12">
          <h1 className="text-4xl font-black uppercase tracking-tight text-gray-900">Mon Panier</h1>
          <div className="h-1.5 w-20 mt-3 rounded-full" style={{ backgroundColor: colors.primary }} />
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8 space-y-4">
            <AnimatePresence mode='popLayout'>
              {cartItems.map(item => {
                const isOutOfStock = item.stock <= 0;
                return (
                  <motion.div
                    key={item.id}
                    layout
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, x: -20 }}
                    className={`group flex flex-col sm:flex-row items-center gap-6 bg-white p-5 rounded-3xl shadow-sm border border-gray-100 transition-all 
                      ${isOutOfStock ? 'opacity-60' : 'hover:shadow-md'}`}
                  >
                    <Link to={"/product/" + item.slug} className="shrink-0 relative">
                      <div className={`w-28 h-28 bg-gray-50 rounded-2xl overflow-hidden border border-gray-100 ${isOutOfStock ? 'grayscale' : ''}`}>
                        <img src={getImageUrl(item.image)} alt={item.title} className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-500" />
                      </div>
                      {isOutOfStock && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-2xl">
                          <span className="text-[10px] text-white font-black uppercase bg-red-600 px-2 py-1 rounded">Épuisé</span>
                        </div>
                      )}
                    </Link>

                    <div className="flex-1 text-center sm:text-left">
                      <h3 className="font-bold text-lg text-gray-800">{item.title}</h3>
                      {isOutOfStock ? (
                        <p className="text-red-500 text-xs font-bold uppercase tracking-widest">Rupture de stock</p>
                      ) : (
                        <p className="font-medium text-gray-400 text-sm uppercase tracking-widest">{item.price.toFixed(2)} dh</p>
                      )}
                    </div>

                    <div className={`flex items-center bg-gray-50 rounded-2xl p-1 border border-gray-200 ${isOutOfStock ? 'invisible' : ''}`}>
                      <button type="button" onClick={() => updateQuantity(item.id, -1)} className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-white transition-all font-bold text-xl">-</button>
                      <span className="w-12 text-center font-black text-gray-800">{item.quantity}</span>
                      <button
                        type="button"
                        onClick={() => updateQuantity(item.id, 1)}
                        disabled={item.quantity >= item.stock}
                        className={`w-10 h-10 flex items-center justify-center rounded-xl transition-all font-bold text-xl 
                          ${item.quantity >= item.stock ? 'opacity-30 cursor-not-allowed' : 'hover:bg-white'}`}
                      >
                        +
                      </button>
                    </div>

                    <div className="flex flex-col items-center sm:items-end min-w-[100px]">
                      <span className="font-black text-xl" style={{ color: isOutOfStock ? '#9ca3af' : colors.primary }}>
                        {isOutOfStock ? "0.00" : (item.price * item.quantity).toFixed(2)} <span className="text-xs">DH</span>
                      </span>
                      <button type="button" onClick={() => removeItem(item.slug)} className="mt-1 text-xs font-bold uppercase text-red-400 hover:text-red-600">Retirer</button>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>

            {cartItems.length === 0 && (
              <div className="text-center py-24 bg-white rounded-[40px] border-2 border-dashed border-gray-200">
                <ShoppingBag className="mx-auto text-gray-200 mb-4" size={64} />
                <p className="text-gray-400 font-bold text-xl">Votre panier est vide.</p>
                <Link to="/" className="mt-4 inline-block text-orange-500 font-bold underline">Continuer mes achats</Link>
              </div>
            )}
          </div>

          <div className="lg:col-span-4">
            <div className="bg-white p-8 rounded-[32px] shadow-xl border border-gray-50 sticky top-10">
              <h2 className="font-black uppercase text-sm tracking-[0.2em] mb-8 text-gray-400">Récapitulatif</h2>
              <div className="space-y-5 mb-6 text-gray-600 font-bold">
                <div className="flex justify-between"><span>Articles ({cartItems.filter(i => i.stock > 0).length})</span><span>{subtotal.toFixed(2)} dh</span></div>
                <div className="flex justify-between"><span>Retrait au magasin</span><span className="text-green-500 text-xs bg-green-50 px-3 py-1 rounded-full uppercase">Gratuite</span></div>
                <div className="flex justify-between"><span>Livraison à domicile</span><span className="text-pink-500 text-xs bg-pink-50 px-3 py-1 rounded-full uppercase">Payante</span></div>
                
                <div className="pt-5 border-t-2 border-gray-50">
                  <div className="flex justify-between items-end">
                    <span className="font-black text-gray-900">TOTAL</span>
                    <span className="text-3xl font-black" style={{ color: colors.primary }}>{total.toFixed(2)} <span className="text-sm">DH</span></span>
                  </div>
                  <p className="text-sm font-medium text-gray-400 mt-2">Tous les prix sont en dirhams (DH)</p>
                </div>
              </div>

              {/* Guarantees Details Section */}
              <div className="bg-[#FFF9F0] rounded-2xl p-5 space-y-5 mb-6">
                <div className="flex gap-4 items-start">
                  <ShieldCheck className="text-[#FFAA00] shrink-0" size={24} />
                  <div>
                    <h4 className="font-bold text-sm text-gray-900">Garantie officielle constructeur</h4>
                    <p className="text-xs text-gray-500 font-medium mt-1">Produits 100% authentiques et garantis.</p>
                  </div>
                </div>
                <div className="flex gap-4 items-start">
                  <Truck className="text-[#FFAA00] shrink-0" size={24} />
                  <div>
                    <h4 className="font-bold text-sm text-gray-900">Livraison partout au Maroc</h4>
                    <p className="text-xs text-gray-500 font-medium mt-1">Rapide et sécurisée.</p>
                  </div>
                </div>
                <div className="flex gap-4 items-start">
                  <Headphones className="text-[#FFAA00] shrink-0" size={24} />
                  <div>
                    <h4 className="font-bold text-sm text-gray-900">Confirmation rapide par téléphone</h4>
                    <p className="text-xs text-gray-500 font-medium mt-1">Notre équipe vous contacte pour valider votre commande.</p>
                  </div>
                </div>
              </div>

              {!isCheckoutVisible && cartItems.some(i => i.stock > 0) && (
                <div className="space-y-4">
                  <button
                    type="button"
                    onClick={() => {
                      setIsCheckoutVisible(true);
                      setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
                    }}
                    className="w-full flex items-center justify-center gap-2 py-5 rounded-2xl font-black uppercase tracking-widest text-white shadow-xl transition-all"
                    style={{ backgroundColor: colors.primary }}
                  >
                    <Lock size={18} /> COMMANDER MAINTENANT
                  </button>
                  <p className="flex justify-center items-center gap-2 text-sm text-gray-500 font-medium">
                    <Lock size={14} className="text-gray-400" /> Paiement à la livraison
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        <AnimatePresence>
          {isCheckoutVisible && subtotal > 0 && (
            <motion.div ref={formRef} initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} className="mt-16 max-w-3xl mx-auto">
              <div className="bg-white p-8 md:p-12 rounded-[40px] shadow-2xl border border-gray-100">
                <div className="text-center mb-10">
                  <h2 className="text-3xl font-black uppercase text-gray-900">Finaliser ma commande</h2>
                  <p className="text-gray-500 mt-2 font-medium">Paiement à la livraison.</p>
                </div>
                <form onSubmit={handleOrderSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label className="text-[10px] font-black uppercase tracking-widest mb-2 text-gray-400 ml-1">Nom Complet</label>
                    <input type="text" name="name" required placeholder="Votre nom" className="w-full p-4 bg-gray-50 border-2 border-transparent focus:border-orange-400 rounded-2xl outline-none font-bold" />
                  </div>
                  <div>
                    <label className="text-[10px] font-black uppercase tracking-widest mb-2 text-gray-400 ml-1">Téléphone</label>
                    <input type="tel" name="phone" required placeholder="06 00 00 00 00" className="w-full p-4 bg-gray-50 border-2 border-transparent focus:border-orange-400 rounded-2xl outline-none font-bold" />
                  </div>
                  <div>
                    <label className="text-[10px] font-black uppercase tracking-widest mb-2 text-gray-400 ml-1">Ville</label>
                    <input type="text" name="city" required placeholder="Ex: Casablanca" className="w-full p-4 bg-gray-50 border-2 border-transparent focus:border-orange-400 rounded-2xl outline-none font-bold" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-[10px] font-black uppercase tracking-widest mb-2 text-gray-400 ml-1">Email</label>
                    <input type="email" name="email" placeholder="Votre Email" className="w-full p-4 bg-gray-50 border-2 border-transparent focus:border-orange-400 rounded-2xl outline-none font-bold" />
                  </div>

                  <div className="md:col-span-2">
                    <label className="text-[10px] font-black uppercase tracking-widest mb-2 text-gray-400 ml-1">Mode de réception</label>

                    <label className='text-[14px] cursor-pointer flex items-center gap-4 mt-4 font-bold'>
                      Retrait en magasin (Gratuit)
                      <input 
                        onChange={() => setDeliveryMode("magasin")} 
                        checked={deliveryMode === "magasin"}
                        value="Retrait en magasin" 
                        type="radio" 
                        name="mode_reception" 
                        className="w-4 h-4 accent-orange-400" 
                      />
                    </label>

                    <label className='text-[14px] cursor-pointer flex items-center gap-4 mt-4 font-bold'>
                      Livraison à domicile
                      <input 
                        onChange={() => setDeliveryMode("livraison")} 
                        checked={deliveryMode === "livraison"}
                        value="Livraison à domicile" 
                        type="radio" 
                        name="mode_reception" 
                        className="w-4 h-4 accent-orange-400" 
                      />
                    </label>

                    {/* Conditionally rendered based on deliveryMode state */}
                    {deliveryMode === "livraison" && (
                      <div className='mt-4 p-4 bg-gray-50 rounded-2xl border border-gray-100'>
                        <h2 className='text-[14px] text-[#FFAA00] mb-4 font-bold'>
                          Les frais de livraison seront confirmés par téléphone avant la validation et l'expédition de votre commande.
                        </h2>

                        <div className="space-y-4">
                          <div>
                            <label className="text-[10px] font-black uppercase tracking-widest mb-2 text-gray-400 ml-1">Adresse</label>
                            <input type="text" name="adresse" required placeholder="Votre Adresse" className="w-full p-4 bg-white border-2 border-transparent focus:border-orange-400 rounded-2xl outline-none font-bold" />
                          </div>
                          <div>
                            <label className="text-[10px] font-black uppercase tracking-widest mb-2 text-gray-400 ml-1">Quartier</label>
                            <input type="text" name="quartier" required placeholder="Votre Quartier" className="w-full p-4 bg-white border-2 border-transparent focus:border-orange-400 rounded-2xl outline-none font-bold" />
                          </div>
                          <div>
                            <label className="text-[10px] font-black uppercase tracking-widest mb-2 text-gray-400 ml-1">Commentaire</label>
                            <input type="text" name="commentaire" placeholder="Commentaire (facultatif)" className="w-full p-4 bg-white border-2 border-transparent focus:border-orange-400 rounded-2xl outline-none font-bold" />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="md:col-span-2 mt-4">
                    <button type="submit" disabled={isSubmitting} className="w-full py-5 rounded-2xl font-black uppercase tracking-widest text-white shadow-2xl transition-all disabled:opacity-50" style={{ backgroundColor: colors.dark }}>
                      {isSubmitting ? "Validation..." : `Confirmer la commande — ${total.toFixed(2)} DH`}
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default CartPage;